import React from 'react';
import { motion } from 'framer-motion';
import { UserPlus } from 'lucide-react';
import { AdvisorAvatar } from './AdvisorAvatar';

interface Advisor {
  id: string;
  name: string;
  title: string;
  description: string;
  isDefault: boolean;
}

interface AdvisorSelectionProps {
  advisors: Advisor[];
  selectedAdvisors: string[];
  onToggleAdvisor: (advisorId: string) => void;
  onCreateAdvisor: () => void;
  onStartChat: () => void;
}

export const AdvisorSelection: React.FC<AdvisorSelectionProps> = ({
  advisors,
  selectedAdvisors,
  onToggleAdvisor,
  onCreateAdvisor,
  onStartChat
}) => {
  return (
    <div className="flex-1 flex flex-col min-h-0">
      {/* Header */}
      <div className="text-center p-4 sm:p-6 lg:p-8 border-b border-white/10">
        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-3 sm:mb-4 bg-gradient-to-r from-orange-400 to-purple-400 bg-clip-text text-transparent">
          Select Your Advisors
        </h2>
        <p className="text-lg sm:text-xl text-white/80 mb-3 sm:mb-4 px-2">
          Choose one for 1:1 guidance or multiple for group discussion
        </p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6 text-sm text-white/60">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 bg-green-400 rounded-full"></div>
            <span>Group: Chat & Voice</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
            <span>1:1: Chat, Voice & Video</span>
          </div>
        </div>
      </div>

      {/* Advisors Grid - Scrollable */}
      <div className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8 pb-32 lg:pb-8 min-h-0">
        <div className="max-w-4xl mx-auto">
          {advisors.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 rounded-full bg-white/10 flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-white/60" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-white mb-2">No Advisors Available</h3>
              <p className="text-white/70">Loading advisors or none found. Please wait or refresh the page.</p>
            </div>
          ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 mb-6 sm:mb-8">
            {advisors.map((advisor) => (
              <motion.div
                key={advisor.id}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => onToggleAdvisor(advisor.id)}
                className={`p-4 sm:p-6 rounded-3xl border-2 cursor-pointer transition-all duration-300 ${
                  selectedAdvisors.includes(advisor.id)
                    ? 'bg-gradient-to-r from-orange-500/20 to-purple-500/20 border-orange-400'
                    : 'bg-white/5 border-white/20 hover:bg-white/10 hover:border-white/40'
                }`}
              >
                <div className="flex items-center gap-4">
                  <AdvisorAvatar name={advisor.name} size="large" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="text-lg sm:text-xl font-bold text-white truncate">{advisor.name}</h3>
                      {!advisor.isDefault && (
                        <span className="px-2 py-1 rounded-full bg-purple-500/20 text-purple-300 text-xs">Custom</span>
                      )}
                    </div>
                    <p className="text-orange-400 font-medium mb-2 text-sm sm:text-base">{advisor.title}</p>
                    <p className="text-white/70 text-sm">{advisor.description}</p>
                  </div>
                  {selectedAdvisors.includes(advisor.id) && (
                    <div className="w-6 h-6 rounded-full bg-orange-400 flex items-center justify-center flex-shrink-0">
                      <div className="w-2 h-2 bg-white rounded-full"></div>
                    </div>
                  )}
                </div>
              </motion.div>
            ))}

            {/* Create New Advisor Card */}
            <motion.div
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={onCreateAdvisor}
              className="p-4 sm:p-6 rounded-3xl border-2 border-dashed border-white/30 cursor-pointer transition-all duration-300 hover:border-purple-400 hover:bg-purple-500/10 flex items-center justify-center"
            >
              <div className="text-center">
                <UserPlus className="w-8 h-8 sm:w-12 sm:h-12 text-purple-400 mx-auto mb-3" />
                <h3 className="text-base sm:text-lg font-bold text-white mb-2">Create Your New Advisor</h3>
                <p className="text-white/60 text-sm">Add your own specialized advisor</p>
              </div>
            </motion.div>
          </div>
          )}
        </div>
      </div>

      {/* Fixed Start Chat Button */}
      <div className="fixed bottom-0 left-0 right-0 lg:static bg-black/90 backdrop-blur-sm border-t border-white/10 p-4 sm:p-6 z-40">
        <div className="max-w-4xl mx-auto text-center">
          <motion.button
            whileHover={{ scale: selectedAdvisors.length > 0 ? 1.05 : 1 }}
            whileTap={{ scale: selectedAdvisors.length > 0 ? 0.95 : 1 }}
            onClick={onStartChat}
            disabled={selectedAdvisors.length === 0}
            className={`w-full sm:w-auto px-8 sm:px-12 py-3 sm:py-4 rounded-full font-bold text-base sm:text-lg transition-all duration-300 shadow-lg ${
              selectedAdvisors.length > 0
                ? 'bg-gradient-to-r from-orange-500 to-purple-500 text-white hover:from-orange-400 hover:to-purple-400'
                : 'bg-white/10 text-white/50 cursor-not-allowed'
            }`}
          >
            {selectedAdvisors.length === 0 
              ? 'Select advisors to start' 
              : `Start ${selectedAdvisors.length === 1 ? '1:1 Chat' : 'Group Chat'} (${selectedAdvisors.length} selected)`
            }
          </motion.button>
          
          {selectedAdvisors.length > 0 && (
            <p className="text-white/60 text-sm mt-3">
              Ready to chat with your selected advisor{selectedAdvisors.length > 1 ? 's' : ''}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};